import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Avatar, Chip,
  Fade, Badge, Tooltip
} from '@mui/material';
import {
  SmartToy, Send, Close, Remove, Bolt, AutoAwesome
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const MotionBox = motion(Box);

const ChatbotWidget = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
  if (open && messages.length === 0) {
    const name = user?.full_name?.split(" ")[0] || "there";

    setMessages([
      {
        id: 1,
        type: "bot",
        text: isAuthenticated
          ? `Hey ${name}! I'm your AI assistant. I can find items, track orders, suggest prices, and more. What can I help with?`
          : "Hi there! I'm SpareConnect's AI assistant. Ask me about buying, selling, or how the platform works.",
        suggestions: isAuthenticated
          ? [
              "Find items for me",
              "Track my orders",
              "What's trending?",
              "Help me sell",
            ]
          : [
              "What is SpareConnect?",
              "How to buy?",
              "How to sell?",
              "Is it free?",
            ],
      },
    ]);
  }

  if (open) {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  }
}, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text = null) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { id: Date.now(), type: 'user', text: msg }]);
    setInput('');
    setLoading(true);

    try {
      const endpoint = isAuthenticated ? '/api/chatbot/chat' : '/api/chatbot/chat-public';
      const res = await api.post(endpoint, { message: msg });
      setMessages(prev => [...prev, {
        id: Date.now() + 1, type: 'bot',
        text: res.data.reply,
        suggestions: res.data.suggestions || [],
        items: res.data.items || null,
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now() + 1, type: 'bot',
        text: "Oops! I couldn't connect. Try again in a moment.",
        suggestions: ["Try again"],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* ═══ TRIGGER BUTTON ═══ */}
      <AnimatePresence>
        {!open && (
          <MotionBox
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9999 }}
          >
            <Tooltip title="Chat with AI" placement="left" arrow>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  width: 64, height: 64, position: 'relative',
                  background: 'linear-gradient(145deg, #4f46e5 0%, #7c3aed 50%, #06b6d4 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 4s ease infinite',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(79,70,229,0.45), 0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 12px 40px rgba(79,70,229,0.55), 0 4px 12px rgba(0,0,0,0.15)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '@keyframes gradientShift': {
                    '0%, 100%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                  },
                }}
              >
                <AutoAwesome sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>
            {/* Ripple ring */}
            <Box sx={{
              position: 'absolute', inset: -6, borderRadius: '50%', pointerEvents: 'none',
              border: '2px solid rgba(79,70,229,0.25)',
              animation: 'ripple 2.5s ease-out infinite',
              '@keyframes ripple': {
                '0%': { transform: 'scale(0.9)', opacity: 1 },
                '100%': { transform: 'scale(1.5)', opacity: 0 },
              },
            }} />
            <Box sx={{
              position: 'absolute', inset: -6, borderRadius: '50%', pointerEvents: 'none',
              border: '2px solid rgba(79,70,229,0.15)',
              animation: 'ripple 2.5s ease-out infinite 1.2s',
            }} />
          </MotionBox>
        )}
      </AnimatePresence>

      {/* ═══ CHAT WINDOW ═══ */}
      <AnimatePresence>
        {open && (
          <MotionBox
            initial={{ opacity: 0, y: 30, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.92 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            sx={{
              position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
              width: { xs: 'calc(100vw - 24px)', sm: 400 },
              height: { xs: 'calc(100vh - 80px)', sm: 580 },
              maxWidth: 420, maxHeight: 620,
            }}
          >
            <Paper sx={{
              height: '100%', display: 'flex', flexDirection: 'column',
              borderRadius: 5, overflow: 'hidden',
              border: '1px solid rgba(79,70,229,0.12)',
              boxShadow: '0 25px 65px rgba(15,23,42,0.2), 0 8px 20px rgba(79,70,229,0.08), 0 0 0 1px rgba(79,70,229,0.04)',
            }}>

              {/* ── Header ── */}
              <Box sx={{
                background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 50%, #3730a3 100%)',
                p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5,
                position: 'relative', overflow: 'hidden',
              }}>
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, right: 40, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />

                <Box sx={{ position: 'relative' }}>
                  <Avatar sx={{
                    width: 42, height: 42,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))',
                    border: '1.5px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                  }}>
                    <Bolt sx={{ fontSize: 22, color: '#c7d2fe' }} />
                  </Avatar>
                  {/* Online dot */}
                  <Box sx={{ position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, borderRadius: '50%', bgcolor: '#34d399', border: '2px solid #312e81' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '-0.01em' }}>
                    SpareConnect AI
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(199,210,254,0.8)', fontSize: '0.7rem' }}>
                    Powered by AI • Always here to help
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpen(false)} sx={{
                  color: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: 'white' },
                  width: 30, height: 30,
                }}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              {/* ── Messages Area ── */}
              <Box sx={{
                flex: 1, overflow: 'auto', p: 2,
                background: 'linear-gradient(180deg, #f8faff 0%, #fafbff 100%)',
                display: 'flex', flexDirection: 'column', gap: 2,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#c7d2fe', borderRadius: 10 },
              }}>
                {messages.map((msg, idx) => (
                  <MotionBox
                    key={msg.id}
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.25, delay: idx === messages.length - 1 ? 0.05 : 0 }}
                  >
                    {/* Message bubble */}
                    <Box sx={{ display: 'flex', justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 0.8 }}>
                      {msg.type === 'bot' && (
                        <Avatar sx={{ width: 26, height: 26, bgcolor: '#eef2ff', mb: 0.5 }}>
                          <AutoAwesome sx={{ fontSize: 14, color: '#4f46e5' }} />
                        </Avatar>
                      )}
                      <Box sx={{
                        maxWidth: '82%', py: 1.3, px: 1.8,
                        borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        bgcolor: msg.type === 'user'
                          ? 'linear-gradient(135deg, #4f46e5, #6366f1)'
                          : 'white',
                        background: msg.type === 'user' ? 'linear-gradient(135deg, #4f46e5, #6366f1)' : 'white',
                        color: msg.type === 'user' ? 'white' : '#1e293b',
                        boxShadow: msg.type === 'bot'
                          ? '0 2px 12px rgba(0,0,0,0.04), 0 0 0 1px rgba(148,163,184,0.08)'
                          : '0 2px 12px rgba(79,70,229,0.15)',
                      }}>
                        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.65, whiteSpace: 'pre-line', fontWeight: msg.type === 'user' ? 400 : 400 }}>
                          {msg.text}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Item results */}
                    {msg.items && msg.items.length > 0 && (
                      <Box sx={{ ml: 4.5, mt: 1.2, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {msg.items.map((item) => (
                          <Box
                            key={item.id}
                            onClick={() => { navigate(`/item/${item.id}`); setOpen(false); }}
                            sx={{
                              p: 1.5, borderRadius: 2.5, bgcolor: 'white',
                              border: '1px solid rgba(148,163,184,0.12)',
                              cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              transition: 'all 0.2s ease',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
                              '&:hover': { borderColor: '#a5b4fc', boxShadow: '0 4px 12px rgba(79,70,229,0.08)', transform: 'translateX(4px)' },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.name}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.2 }}>
                                {item.category} • {item.condition}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#4f46e5', whiteSpace: 'nowrap', ml: 1.5 }}>
                              ₹{item.price?.toLocaleString()}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Suggestion chips */}
                    {msg.suggestions && msg.suggestions.length > 0 && msg.type === 'bot' && (
                      <Box sx={{ ml: 4.5, mt: 1.2, display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {msg.suggestions.map((sug, i) => (
                          <Chip
                            key={i} label={sug} size="small"
                            onClick={() => sendMessage(sug)}
                            sx={{
                              height: 28, fontSize: '0.7rem', fontWeight: 500,
                              bgcolor: 'rgba(79,70,229,0.04)', color: '#4f46e5',
                              border: '1px solid rgba(79,70,229,0.1)',
                              cursor: 'pointer', transition: 'all 0.2s ease',
                              '&:hover': { bgcolor: 'rgba(79,70,229,0.1)', borderColor: 'rgba(79,70,229,0.25)', transform: 'translateY(-1px)' },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </MotionBox>
                ))}

                {/* Typing indicator */}
                {loading && (
                  <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.8 }}>
                    <Avatar sx={{ width: 26, height: 26, bgcolor: '#eef2ff' }}>
                      <AutoAwesome sx={{ fontSize: 14, color: '#4f46e5' }} />
                    </Avatar>
                    <Box sx={{
                      display: 'flex', gap: 0.5, py: 1.4, px: 2, bgcolor: 'white', borderRadius: '16px 16px 16px 4px',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.04), 0 0 0 1px rgba(148,163,184,0.08)',
                    }}>
                      {[0, 1, 2].map(i => (
                        <Box key={i} sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          bgcolor: '#a5b4fc',
                          animation: 'typing 1.4s infinite',
                          animationDelay: `${i * 0.15}s`,
                          '@keyframes typing': {
                            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
                            '30%': { transform: 'translateY(-5px)', opacity: 1 },
                          },
                        }} />
                      ))}
                    </Box>
                  </MotionBox>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* ── Input Area ── */}
              <Box sx={{
                p: 1.5, borderTop: '1px solid rgba(148,163,184,0.08)',
                bgcolor: 'white', display: 'flex', gap: 1, alignItems: 'flex-end',
              }}>
                <TextField
                  ref={inputRef}
                  fullWidth size="small" placeholder="Type a message..."
                  value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  multiline maxRows={3}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3, bgcolor: '#f8faff', fontSize: '0.84rem',
                      py: 0.3,
                      '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                      '&:hover fieldset': { borderColor: '#c7d2fe' },
                      '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: 1.5 },
                    },
                  }}
                />
                <IconButton
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  sx={{
                    width: 40, height: 40, borderRadius: 2.5, flexShrink: 0,
                    background: input.trim() && !loading
                      ? 'linear-gradient(135deg, #4f46e5, #6366f1)'
                      : 'rgba(148,163,184,0.1)',
                    color: input.trim() && !loading ? 'white' : '#94a3b8',
                    boxShadow: input.trim() && !loading ? '0 4px 12px rgba(79,70,229,0.3)' : 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': input.trim() ? { transform: 'scale(1.05)', boxShadow: '0 6px 18px rgba(79,70,229,0.4)' } : {},
                    '&.Mui-disabled': { color: '#cbd5e1', background: 'rgba(148,163,184,0.08)' },
                  }}
                >
                  <Send sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>

              {/* Footer */}
              <Box sx={{ px: 2, py: 0.8, bgcolor: '#fafbff', borderTop: '1px solid rgba(148,163,184,0.06)', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 500, letterSpacing: '0.03em' }}>
                  Powered by SpareConnect AI &bull; Responses may vary
                </Typography>
              </Box>
            </Paper>
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;